import {DatePicker, Input, Radio, TimePicker} from 'antd'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {WeekdaysField} from 'components/form/fields/WeekdaysField'
import {createValue} from 'components/form/utils'
import {Formik, FormikConsumer} from 'formik'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import {SelectWidget} from '../../custom/ReactSelectComponents'
import {createControlledFormField} from '../../factory/createFormField'
import {IoCalendarOutline} from 'react-icons/io5'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {IoIosTimer} from 'react-icons/io'
import {renderElse} from '../../../Shared'

dayjs.extend(customParseFormat)

const specialEventOptions = [
  {value: 0, label: 'normal mass'},
  {value: 1, label: 'special mass'},
]

const massOptions = [
  {value: 1, label: 'sunday mass'},
  {value: 0, label: 'daily mass'},
]

export const BooleanPicker = ({
  name,
  value = false,
  onChange,
  defaultValue,
  disabled = false,
  options = specialEventOptions,
}) =>
  renderElse(
    _.isEmpty(options),
    <Translate>
      {(t) => (
        <Radio.Group
          name={name}
          value={value}
          disabled={disabled}
          buttonStyle="solid"
          className="custom-radio-button"
          defaultValue={defaultValue}
          onChange={(event) => {
            onChange(createValue(name, event.target.value))
          }}>
          {Array.from(options ?? []).map(({label, value}) => (
            <React.Fragment key={value}>
              <Radio.Button value={value}>{t(label)}</Radio.Button>
            </React.Fragment>
          ))}
        </Radio.Group>
      )}
    </Translate>
  )

const SectionLabel = ({children}) => (
  <div className="flex items-center gap-2">
    <span className="text-2xs font-bold text-slate-400 uppercase tracking-widest leading-none flex-shrink-0">
      {children}
    </span>
    <div className="flex-1 border-t border-color-50" />
  </div>
)

const formSchema = {
  title: {
    title: 'title',
    children: [
      {
        name: 'title',
        component: createControlledFormField({
          placeholder: 'title',
          Component: Input,
        }),
      },
    ],
  },
  description: {
    title: 'description',
    children: [
      {
        name: 'description',
        component: createControlledFormField({
          rows: 3,
          placeholder: 'description',
          Component: Input.TextArea,
        }),
      },
    ],
  },
  language: {
    title: 'language',
    children: [
      {
        name: 'language',
        component: ({name, value, onChange}) => (
          <Translate>
            {(t) => (
              <SelectWidget
                name={name}
                value={value}
                allowClear={true}
                className="w-full"
                defaultValue="vi"
                placeholder={t('language')}
                onChange={(value) => {
                  onChange(createValue(name, value ?? null))
                }}
                options={[
                  {label: t('vi'), value: 'vi'},
                  {label: t('en'), value: 'en'},
                  {label: t('fr'), value: 'fr'},
                  {label: t('zh'), value: 'zh'},
                  {label: t('ko'), value: 'ko'},
                ]}
              />
            )}
          </Translate>
        ),
      },
    ],
  },
  special_event_flag: {
    title: 'mass type',
    children: [
      {
        name: 'special_event_flag',
        component: (props) => <BooleanPicker options={specialEventOptions} {...props} />,
      },
    ],
  },
  special_event_datetime: {
    title: 'event date time',
    children: [
      {
        name: 'special_event_datetime',
        component: ({name, value, onChange}) => (
          <Translate>
            {(t) => (
              <DatePicker
                showTime
                name={name}
                className="w-full"
                format={'HH:mm - DD/MM/YYYY'}
                value={value ? dayjs(new Date(value)) : null}
                suffixIcon={<IoCalendarOutline className="text-color-500" />}
                placeholder={t('ex: 07:00 - 20/10/2010')}
                onChange={(date) => {
                  onChange(createValue(name, date ? date.format() : undefined))
                }}
              />
            )}
          </Translate>
        ),
      },
    ],
  },
}

const MassTimeFormBody = ({submitText}) => {
  const t = useTranslate()
  return (
    <div className="flex flex-col space-y-10">
      {/* Section 1: Basic info */}
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('basic information')}</SectionLabel>
        {/* Title (flex-1) + Language (fixed 120px) on same row */}
        <div
          className="grid gap-3"
          style={{gridTemplateColumns: '1fr 120px'}}>
          <FieldsFactory formSchema={[formSchema.title]} />
          <FieldsFactory formSchema={[formSchema.language]} />
        </div>
      </div>

      {/* Section 2: Schedule */}
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('mass times')}</SectionLabel>

        {/* Mass type: normal / special — always shown */}
        <FieldsFactory formSchema={[formSchema.special_event_flag]} />

        {/* Conditional fields based on mass type */}
        <FormikConsumer>
          {({values, setFieldValue}) => {
            if (values.special_event_flag) {
              return (
                <FieldsFactory formSchema={[formSchema.special_event_datetime]} />
              )
            }

            const weekdaysStr = values.weekdays ?? '0000000'

            return (
              <div className="flex flex-col gap-3">
                {/* Mass category (flex-1) + Start time (fixed 110px) */}
                <div
                  className="grid gap-3"
                  style={{gridTemplateColumns: '1fr 110px'}}>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-color-200">
                      {t('mass category')}
                    </span>
                    <BooleanPicker
                      name="sunday_mass_flag"
                      value={values.sunday_mass_flag}
                      options={massOptions}
                      onChange={(e) => setFieldValue(e.target.name, e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-color-200">
                      {t('start time')}
                    </span>
                    <TimePicker
                      value={values.start_time ? dayjs(values.start_time, 'HH:mm') : undefined}
                      className="w-full"
                      format="HH:mm"
                      changeOnScroll
                      needConfirm={false}
                      suffixIcon={<IoIosTimer className="text-color-500" />}
                      placeholder={t('ex: 07:00')}
                      onChange={(_, timeString) => setFieldValue('start_time', timeString)}
                    />
                  </div>
                </div>

                {/* Weekdays picker */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-color-200">
                    {t('recurring days')}
                  </span>
                  <WeekdaysField
                    value={weekdaysStr}
                    onChange={(next) => setFieldValue('weekdays', next)}
                  />
                </div>
              </div>
            )
          }}
        </FormikConsumer>
      </div>

      {/* Section 3: Notes */}
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('notes')}</SectionLabel>
        <FieldsFactory formSchema={[formSchema.description]} />
      </div>

      <FormActionBar submitText={submitText} />
    </div>
  )
}

const validate = (values, t) => {
  const errors = {}
  _.forEach(['title', 'language'], (name) => {
    const value = _.get(values, name)
    if (_.isEmpty(value ?? undefined)) {
      errors[name] = t('required field')
    }
  })
  return errors
}

export const CreateChurchMassTimeForm = ({
  onSubmit = NullAsDefault,
}) => {
  const t = useTranslate()
  return (
    <Formik
      initialValues={{
        language: 'vi',
        title: undefined,
        description: undefined,
        weekdays: '0000000',
        start_time: '07:00',
        sunday_mass_flag: 0,
        special_event_flag: 0,
        special_event_datetime: undefined,
      }}
      validate={(values) => validate(values, t)}
      validateOnMount={true}
      validateOnChange={true}
      enableReinitialize={true}
      onSubmit={(values) => {
        onSubmit(_.omitBy(values, _.isUndefined))
      }}>
      <MassTimeFormBody submitText="create" />
    </Formik>
  )
}

export const EditChurchMassTimeForm = ({
  item,
  onSubmit = NullAsDefault,
}) => {
  const t = useTranslate()
  const initialValues = useMemo(() => ({...item}), [item])
  return (
    <Formik
      validate={(values) => validate(values, t)}
      validateOnMount={true}
      validateOnChange={true}
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={(values) => {
        const change = shallowDiff(values, initialValues)
        onSubmit(_.omitBy(change, _.isUndefined))
      }}>
      <MassTimeFormBody submitText="save" />
    </Formik>
  )
}